/**
 * Cash Casino — the slot machine's rules, pure. Plan §6.4.
 *
 * 3×3 reels, 5 paylines (three rows + two diagonals), paytable in TOTAL
 * RETURN on the whole stake (X-doctrine). Top line is X25 in v1: a line that
 * pays more than ≈ 0.25× a quarter's expected profit can never be paid by the
 * per-quarter engine at a ₹100 stake, so the printed paytable is the
 * REACHABLE one. No free spins (a zero-stake round is the classic rigged tell).
 *
 * Reel STRIPS carry the natural prior π(c): each stop is one candidate; the
 * server's scorer (k_solo = 20) picks among them.
 */

export type Symbol = 'seven' | 'bell' | 'diamond' | 'bar' | 'cherry' | 'ball' | 'blank';
export const SYMBOLS: readonly Symbol[] = ['seven', 'bell', 'diamond', 'bar', 'cherry', 'ball', 'blank'];

/** three-of-a-kind line pays (total return); any-two sevens X2; scatter X8 */
export const LINE_X: Record<Exclude<Symbol, 'ball' | 'blank'>, number> = { seven: 25, bell: 20, diamond: 15, bar: 10, cherry: 5 };
export const TWO_SEVENS_X = 2;
export const SCATTER_X = 8;                        // three balls anywhere on the grid

/** 32-stop strips, 9 blanks each, tuned by exact enumeration to 95.08% RTP
 *  (hit rate 16%, three-sevens 0.12%): cherry 8, bar 4, bell 3, diamond 3, seven 2, ball 3, blank 9 */
const S: Symbol[][] = [
  ['blank', 'bell', 'diamond', 'cherry', 'blank', 'ball', 'bar', 'blank', 'cherry', 'bell', 'seven', 'blank', 'cherry', 'diamond', 'blank', 'cherry', 'bar', 'ball', 'blank', 'cherry', 'bell', 'blank', 'bar', 'cherry', 'diamond', 'blank', 'ball', 'cherry', 'blank', 'seven', 'bar', 'cherry'],
  ['cherry', 'blank', 'seven', 'bar', 'cherry', 'blank', 'bell', 'diamond', 'cherry', 'blank', 'ball', 'bar', 'blank', 'cherry', 'bell', 'seven', 'blank', 'cherry', 'diamond', 'blank', 'cherry', 'bar', 'ball', 'blank', 'cherry', 'bell', 'blank', 'bar', 'cherry', 'diamond', 'blank', 'ball'],
  ['blank', 'bar', 'cherry', 'diamond', 'blank', 'ball', 'cherry', 'blank', 'seven', 'bar', 'cherry', 'blank', 'bell', 'diamond', 'cherry', 'blank', 'ball', 'bar', 'blank', 'cherry', 'bell', 'seven', 'blank', 'cherry', 'diamond', 'blank', 'cherry', 'bar', 'ball', 'blank', 'cherry', 'bell'],
];
export const STOPS = 32;
export const REELS = S;

export type Grid = Symbol[][];                     // grid[reel][row], row 0 = top

/** the 3 visible symbols per reel for a stop triple */
export function gridFor(stops: [number, number, number]): Grid {
  return stops.map((st, r) => [0, 1, 2].map((row) => S[r][(st + row) % STOPS]));
}

/** paylines as [row on reel0, row on reel1, row on reel2] */
export const PAYLINES: [number, number, number][] = [[1, 1, 1], [0, 0, 0], [2, 2, 2], [0, 1, 2], [2, 1, 0]];

export type LineWin = { line: number; symbol: Symbol | 'two-sevens'; x: number };
export type Evaluation = { lines: LineWin[]; scatter: boolean; totalX: number };

export function evaluate(grid: Grid): Evaluation {
  const lines: LineWin[] = [];
  PAYLINES.forEach((pl, i) => {
    const a = grid[0][pl[0]], b = grid[1][pl[1]], c = grid[2][pl[2]];
    if (a === b && b === c && a !== 'ball' && a !== 'blank') lines.push({ line: i, symbol: a, x: LINE_X[a] });
    else if ([a, b, c].filter((s) => s === 'seven').length === 2) lines.push({ line: i, symbol: 'two-sevens', x: TWO_SEVENS_X });
  });
  const balls = grid.flat().filter((s) => s === 'ball').length;
  const scatter = balls >= 3;
  const totalX = lines.reduce((t, l) => t + l.x, 0) + (scatter ? SCATTER_X : 0);
  return { lines, scatter, totalX };
}

/** what the spin pays: floor(stake × Σ X) */
export const payout = (stake: number, ev: Evaluation) => Math.floor(stake * ev.totalX + 1e-9);

/** natural spin: uniform stops. PHASE-2 SEAM: the server's scorer picks the triple. */
export function spin(rng: () => number): [number, number, number] {
  return [Math.floor(rng() * STOPS), Math.floor(rng() * STOPS), Math.floor(rng() * STOPS)];
}

/** exact RTP of the uniform prior (32³ = 32,768 candidates) — the gate prints it */
export function exactRtp(): { rtp: number; hitRate: number; top: number } {
  let sum = 0, hits = 0, top = 0;
  for (let a = 0; a < STOPS; a++) for (let b = 0; b < STOPS; b++) for (let c = 0; c < STOPS; c++) {
    const ev = evaluate(gridFor([a, b, c]));
    sum += ev.totalX; if (ev.totalX > 0) hits++;
    if (ev.lines.some((l) => l.symbol === 'seven')) top++;
  }
  const n = STOPS ** 3;
  return { rtp: sum / n, hitRate: hits / n, top: top / n };
}

export const fmtX = (x: number) => `X${(Math.floor(x * 100 + 1e-9) / 100).toFixed(2)}`;
